import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import { Spin, Empty } from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Formatter as LegendFormatter } from "recharts/types/component/DefaultLegendContent";
import { Formatter as TooltipFormatter } from "recharts/types/component/DefaultTooltipContent";
import styled from "styled-components";
import { calculateTotalSessionsAnalyticsPerMonth } from "../../../helper/analyticsHelper";

import { useSessionsAnalytics } from "../../../hooks/analytics/useSessionsAnalytics";

const ChartWrapper = styled.div`
  width: 100%;
  height: 300px;
`;

const CHART_COLOR_completed = "#60C325";
const CHART_COLOR_DROPPED = "#EB0000";

type SessionsChartProps = {
  startTimestamp?: Date;
  endTimestamp?: Date;
};

export const SessionsChart: React.FC<SessionsChartProps> = ({
  startTimestamp,
  endTimestamp,
}) => {
  const { t } = useTranslation();

  const { dailySummary, isLoading } = useSessionsAnalytics(
    startTimestamp,
    endTimestamp
  );

  const totalSessionsPerMonth =
    calculateTotalSessionsAnalyticsPerMonth(dailySummary);

  const chartData = totalSessionsPerMonth.map((value) => {
    return {
      name: dayjs(new Date(value.year, value.month)).format("MMM YY"),
      dropped: value.total.dropped,
      completed: value.total.completed,
    };
  });

  const legendFormatter: LegendFormatter = (value, entry) => {
    const { color } = entry;
    return (
      <span style={{ color }}>
        {t(`dashboard.performance.chart.sessions.label.${value}` as any)}
      </span>
    );
  };

  const tooltipFormatter: TooltipFormatter<number, string> = (value, name) => {
    return [
      value,
      t(`dashboard.performance.chart.sessions.label.${name}` as any),
    ];
  };

  if (isLoading) {
    return <Spin />;
  }

  return (
    <ChartWrapper data-cy-id="dashboard-performance-chart">
      {!isEmpty(dailySummary) ? (
        <ResponsiveContainer>
          <AreaChart width={500} height={300} data={chartData}>
            <Area
              type="linear"
              dataKey="completed"
              stroke={CHART_COLOR_completed}
              fillOpacity={0.25}
              fill={CHART_COLOR_completed}
            />
            <Area
              type="linear"
              dataKey="dropped"
              stroke={CHART_COLOR_DROPPED}
              fillOpacity={0.25}
              fill={CHART_COLOR_DROPPED}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <Empty
          image={null}
          description={t(`dashboard.performance.chart.empty`)}
        />
      )}
    </ChartWrapper>
  );
};
